import { isMfaStepUpChallenge } from '../utils/admin-verification.utils';
import { inject } from "@angular/core";
import {
  HttpRequest,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpHandlerFn,
} from "@angular/common/http";
import { throwError, Observable } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { AuthService } from "../services/auth.service";
import { MfaStepUpService } from "../services/mfa-step-up.service";
import { Router } from "@angular/router";
import { apiRequestUrl, isSessionNeutralRequest } from './api-request-policy';

// GAP-FE-M005 · Methods that are RFC-7231 "safe" and therefore do
// not require CSRF protection. The retry path uses this as an
// allow-list — any method NOT in this set (POST, PUT, PATCH, DELETE,
// custom verbs, WebDAV-style MOVE/PROPPATCH, etc.) fails closed when
// the rotated CSRF token is missing post-refresh. Allow-list rather
// than deny-list because a deny-list silently misses any verb the
// FE wasn't enumerated for.
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

// `admin_mfa_layer` also protects these control-plane mutations even though
// they intentionally live outside the `/api/v1/admin/*` namespace. Keep this
// list synchronized with the mutation routers in allocation.rs and
// model_shadow_runtime.rs. Method-scoping prevents an unrelated read 403 from
// opening an MFA prompt.
const MFA_STEP_UP_POST_PATHS = new Set([
  "/api/v1/research/model-shadow-runtimes/incumbent-designations",
  "/api/v1/allocation/policy/approve",
  "/api/v1/allocation/policy/rollback",
  "/api/v1/allocation/enforcement/stage",
  "/api/v1/allocation/enforcement/activate",
  "/api/v1/allocation/enforcement/disable",
]);
const MODEL_SHADOW_PRIVILEGED_RUNTIME_PATH =
  /^\/api\/v1\/research\/model-shadow-runtimes\/[^/]+\/(?:suspend|designate-live-ready)$/;
function requiresMfaStepUp(method: string, pathname: string): boolean {
  if ((pathname.startsWith("/api/v1/admin/") || pathname.startsWith("/api/v1/admin-secure/"))) {
    return true;
  }

  return (
    method.toUpperCase() === "POST" &&
    (MFA_STEP_UP_POST_PATHS.has(pathname) ||
      MODEL_SHADOW_PRIVILEGED_RUNTIME_PATH.test(pathname))
  );
}


/**
 * Authentication Interceptor (Functional for Angular 17+)
 *
 * Handles:
 * - Adding auth tokens to requests
 * - CSRF token support
 * - Automatic token refresh on 401
 * - Public endpoint exclusion
 * - Cookie-based auth support
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const mfaStepUpService = inject(MfaStepUpService);
  const verificationNavigationId = mfaStepUpService.navigationId;
  const verificationProofVersion = mfaStepUpService.proofVersion;

  const url = apiRequestUrl(req.url);
  if (!url) return next(req);
  const pathname = url.pathname;
  const sessionNeutral = isSessionNeutralRequest(req.method, pathname);

  const isAuthPost =
    sessionNeutral && req.method === "POST" &&
    (pathname.endsWith("/auth/logout") || pathname.endsWith("/auth/refresh"));
  const needsJsonContentType = isAuthPost && !req.headers.has("Content-Type");
  const needsAuthBody = isAuthPost && pathname.endsWith("/auth/logout") && req.body == null;

  if (needsJsonContentType) {
    req = req.clone({
      setHeaders: {
        "Content-Type": "application/json",
      },
    });
  }

  if (needsAuthBody) {
    req = req.clone({
      body: {},
    });
  }

  if (sessionNeutral) {
    return next(req);
  }

  // COLD-LOAD-RACE FIX: attach Bearer + CSRF to a request. Used both for the
  // initial send and the post-refresh retry so the logic stays identical.
  const withAuthHeaders = (
    request: HttpRequest<any>,
    accessToken: string | null,
  ): HttpRequest<any> => {
    if (accessToken) {
      const headers: { [key: string]: string } = {
        Authorization: `Bearer ${accessToken}`,
      };
      const csrf = authService.getCsrfToken();
      if (csrf) {
        headers["X-CSRF-TOKEN"] = csrf;
      }
      return request.clone({
        setHeaders: headers,
        withCredentials: true, // Required for HttpOnly cookie support
      });
    }
    // No token available — still include credentials so the HttpOnly cookie
    // path works (e.g. when there is no active session to refresh from).
    return request.clone({ withCredentials: true });
  };

  // COLD-LOAD-RACE FIX (step 1: queue-before-send).
  // On a cold load the in-memory access token is null until the refresh
  // cookie is exchanged via /auth/refresh. Previously each early authenticated
  // request (/tenants, /permissions, /roles, effective-permissions) fired
  // token-less, 401'd, and independently triggered a refresh — racing each
  // other and cascading to /unauthorized. ensureAccessToken() returns a valid
  // token immediately if one exists, or performs a SINGLE-FLIGHT refresh that
  // all concurrent callers share, then emits the fresh token. Requests thus
  // queue behind one refresh and send WITH the token instead of racing it.
  return authService
    .ensureAccessToken()
    .pipe(
      switchMap((token: string) =>
        sendWithRetry(withAuthHeaders(req, token || null)),
      ),
    );

  // COLD-LOAD-RACE FIX (step 2: refresh-then-retry-once).
  // If an authenticated request still 401s (token expired in flight, or no
  // token was attachable pre-send), trigger a single-flight refresh and retry
  // the original request EXACTLY once with the fresh token. A failed refresh
  // logs out and redirects; a second 401 on the retry propagates to the caller
  // as an ordinary endpoint error (the session survives — see below). We
  // never refresh/retry /auth/refresh or /auth/login themselves (filtered out
  // by the public-path check above), so there is no recursion/deadlock.
  function sendWithRetry(authedReq: HttpRequest<any>): Observable<any> {
    return next(authedReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // Post-logout stragglers (e.g. the audit service's buffered batch
          // flush) must not re-enter the refresh path: for a terminated
          // session refreshTokenSingleFlight() still acquires the cross-tab
          // `razor_token_refresh` Web Lock and replays the request token-less
          // — pure churn against a session that can never refresh (observed
          // live 2026-07-05: post-logout POST /audit/logs 401s each grabbed
          // the lock). Mirror ensureAccessToken()'s pre-send short-circuit
          // and fail fast with the same quiet typed 401 the refresh-failure
          // path below emits. errorInterceptor silences the toast/Sentry and
          // OWNS the recovery: this 'Session expired' message parses to
          // TOKEN_EXPIRED → AuthRecoveryAction.LOGOUT → logout()+redirect —
          // so the /auth/login redirect is guaranteed even in the rare case
          // the latch was set by a sibling tab's broadcast (logout() here is
          // idempotent via the same latch, cf. auth.service logout()).
          if (authService.isSessionInactive()) {
            return throwError(
              () =>
                new HttpErrorResponse({
                  error: { message: "Session expired" },
                  status: 401,
                  statusText: "Session Expired",
                  url: req.url ?? undefined,
                }),
            );
          }
          return authService.refreshTokenSingleFlight().pipe(
            // LOGOUT-BUG FIX (2026-07-18): logout belongs to REFRESH failure only.
            // This catchError sits BEFORE the switchMap so it observes errors from
            // refreshTokenSingleFlight() alone. Previously it sat after the
            // switchMap, so a 401 on the POST-REFRESH RETRY of the original
            // request (e.g. one backend route validating with a divergent JWT
            // secret — the /api/v1/admin-secure 401s that force-logged operators
            // out of the Admin Control Panel) was misclassified as a refresh
            // failure and nuked the whole session. A retry 401 now propagates to
            // the caller as an ordinary endpoint error; the session survives.
            catchError((refreshError) => {
              // Token refresh failed - logout and redirect to login.
              authService.logout();
              router.navigate(["/auth/login"]);
              // ADMIN-INTERFACE-50: previously returned an Observable that COMPLETED
              // WITHOUT EMITTING. HttpClient's observe:'body' filters for HttpResponse,
              // so any firstValueFrom() consumer of this request threw EmptyError
              // ('no elements in sequence'). Emit a catchable, typed 401 instead — a
              // session-expiry is semantically a 401. The outer errorInterceptor
              // silences 401s on non-login URLs (shouldShow401Notification=false ⇒ no
              // toast) and isExpectedHandledNoise drops 401s from Sentry, so this stays
              // quiet after the logout()+redirect above while still giving every
              // consumer a real, handleable error instead of a silent completion.
              void refreshError;
              return throwError(
                () =>
                  new HttpErrorResponse({
                    error: { message: "Session expired" },
                    status: 401,
                    statusText: "Session Expired",
                    url: req.url ?? undefined,
                  }),
              );
            }),
            switchMap((newToken: string) => {
              const headers: { [key: string]: string } = {
                Authorization: `Bearer ${newToken}`,
              };

              // Add CSRF token if available
              const csrfToken = authService.getCsrfToken();
              if (csrfToken) {
                headers["X-CSRF-TOKEN"] = csrfToken;
              } else if (!SAFE_METHODS.has(req.method.toUpperCase())) {
                // GAP-FE-M005 · fail closed: refresh succeeded but the
                // rotated CSRF token did not. For any method outside
                // the RFC-7231 safe set we MUST NOT replay the request
                // without the CSRF half of the double-submit pair —
                // otherwise we hand the backend a possibly-authorized
                // state change that bypasses local CSRF protocol
                // enforcement. Surface a typed 0-status synthetic
                // error so the UI/caller can prompt re-authentication.
                return throwError(
                  () =>
                    new HttpErrorResponse({
                      error: {
                        message:
                          "CSRF token unavailable after refresh; cannot replay request safely.",
                      },
                      status: 0,
                      statusText: "CSRF Token Missing",
                      url: req.url,
                    }),
                );
              }

              // GAP-FE-TESTSUITE · The first pass (above) may have set a
              // now-STALE X-CSRF-TOKEN on `req` from the pre-refresh token.
              // `setHeaders` only adds/overwrites listed headers, so cloning
              // with only Authorization would silently carry the stale CSRF
              // token forward on the retry. For safe methods where the rotated
              // token is absent (csrfToken == null) we must strip it so the
              // replay reflects current CSRF state instead of replaying an
              // invalid double-submit value. When a fresh token exists it is
              // already in `headers` and overwrites the stale one.
              if (!csrfToken && req.headers.has("X-CSRF-TOKEN")) {
                req = req.clone({
                  headers: req.headers.delete("X-CSRF-TOKEN"),
                });
              }

              req = req.clone({
                setHeaders: headers,
                withCredentials: true, // Required for HttpOnly cookie support
              });
              // GAP-FE-M005 · the fail-closed "CSRF Token Missing" synthetic
              // error thrown above, and ANY error from this post-refresh
              // retry (including a second 401), propagate straight to the
              // caller — the refresh-failure catchError above no longer sees
              // them, so they can never be misclassified as a session loss.
              return next(req);
            }),
          );
        }

        // F6 · MFA step-up (reactive). The backend `admin_mfa_layer` protects
        // the admin namespace plus an explicit set of governed research and
        // allocation POST mutations. Prompt only when BOTH the governed route
        // and the backend's explicit challenge header + structured code match.
        // An ordinary admin authorization/tenant 403 must never be mistaken for
        // a security ceremony. On the first exact challenge, establish the
        // httpOnly step-up cookie and retry the original request exactly once.
        if (
          isMfaStepUpChallenge(error) &&
          requiresMfaStepUp(authedReq.method, pathname)
        ) {
          return mfaStepUpService.promptAndStepUp(false, verificationNavigationId, verificationProofVersion).pipe(
            catchError(() => throwError(() => error)),
            // Step-up succeeded → the httpOnly cookie is now set; replay the
            // original request once. Do NOT attach any header.
            switchMap(() =>
              next(authedReq).pipe(
                catchError((retryError: HttpErrorResponse) => {
                  // A 2nd 403 (or any retry error): do not loop — surface the
                  // retry error to the caller.
                  return throwError(() => retryError);
                }),
              ),
            ),
          );
        }

        return throwError(() => error);
      }),
    );
  }
};
